import { useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { GAMES, GAME_KEYS, frequencyTable, uniformity } from '../engine';
import type { GameKey, NumberFrequency, UniformityResult } from '../engine';
import { fetchHistory } from '../lib/api';
import type { DrawRecord } from '../lib/api';
import { formatDateMedium, formatDecimal, formatInt } from '../lib/format';

/** Rolling window used for the drift check. */
const WINDOW_MONTHS = 12;
/** Two consecutive windows below this is the only legitimate alarm. */
const ALERT_P = 0.001;
/** Rows shown before the list is expanded. */
const TOP_ROWS = 10;

type PeriodKey = '3m' | '6m' | '12m' | '24m' | 'all' | 'custom';

const PERIODS: readonly { key: PeriodKey; label: string; months: number | null }[] = [
  { key: '3m', label: '3 mois', months: 3 },
  { key: '6m', label: '6 mois', months: 6 },
  { key: '12m', label: '12 mois', months: 12 },
  { key: '24m', label: '24 mois', months: 24 },
  { key: 'all', label: 'Tout', months: null },
  { key: 'custom', label: 'Choisir…', months: null },
];

type Series = 'main' | 'bonus';
type Order = 'most' | 'least';

function shiftMonths(iso: string, months: number): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1 - months, d)).toISOString().slice(0, 10);
}

function windowed(draws: readonly DrawRecord[], from: string, to: string): DrawRecord[] {
  return draws.filter((x) => x.date >= from && x.date <= to);
}

export function Stats(): ReactElement {
  const [histories, setHistories] = useState<Partial<Record<GameKey, DrawRecord[]>>>({});
  const [selected, setSelected] = useState<GameKey>('loto');
  const [period, setPeriod] = useState<PeriodKey>('12m');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [series, setSeries] = useState<Series>('main');
  const [order, setOrder] = useState<Order>('most');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    for (const key of GAME_KEYS) {
      void fetchHistory(key).then(({ draws }) =>
        setHistories((prev) => ({ ...prev, [key]: draws })),
      );
    }
  }, []);

  const g = GAMES[selected];
  const all = histories[selected];

  // Bounds of what is actually available, used to clamp the custom range.
  const bounds = useMemo(() => {
    if (all === undefined || all.length === 0) return null;
    return { from: (all[0] as DrawRecord).date, to: (all[all.length - 1] as DrawRecord).date };
  }, [all]);

  // Default the custom range to the last three months of what exists.
  useEffect(() => {
    if (bounds === null) return;
    setCustomFrom((v) => (v === '' ? shiftMonths(bounds.to, 3) : v));
    setCustomTo((v) => (v === '' ? bounds.to : v));
  }, [bounds]);

  const range = useMemo(() => {
    if (bounds === null) return null;
    if (period === 'custom') {
      const from = customFrom === '' ? bounds.from : customFrom;
      const to = customTo === '' ? bounds.to : customTo;
      return from <= to ? { from, to } : { from: to, to: from };
    }
    const spec = PERIODS.find((p) => p.key === period);
    if (spec === undefined || spec.months === null) return bounds;
    return { from: shiftMonths(bounds.to, spec.months), to: bounds.to };
  }, [bounds, period, customFrom, customTo]);

  const draws = useMemo(
    () => (all === undefined || range === null ? [] : windowed(all, range.from, range.to)),
    [all, range],
  );

  const nmax = series === 'main' ? g.n : g.bmax;
  const perDraw = series === 'main' ? g.k : g.bk;

  const table = useMemo<NumberFrequency[]>(
    () => frequencyTable(draws.map((d) => (series === 'main' ? d.main : d.bonus)), nmax, perDraw),
    [draws, series, nmax, perDraw],
  );

  const windowTest = useMemo<UniformityResult | null>(
    () =>
      draws.length === 0
        ? null
        : uniformity(draws.map((d) => (series === 'main' ? d.main : d.bonus)), nmax, perDraw),
    [draws, series, nmax, perDraw],
  );

  const drift = useMemo(() => {
    if (all === undefined || all.length === 0) return null;
    const last = (all[all.length - 1] as DrawRecord).date;
    const currentFrom = shiftMonths(last, WINDOW_MONTHS);
    const previousFrom = shiftMonths(last, WINDOW_MONTHS * 2);
    const current = windowed(all, currentFrom, last);
    const previous = windowed(all, previousFrom, currentFrom);
    return {
      current: uniformity(current.map((x) => x.main), g.n, g.k),
      previous:
        previous.length > 0 ? uniformity(previous.map((x) => x.main), g.n, g.k) : null,
    };
  }, [all, g]);

  const ordered = order === 'most' ? table : [...table].reverse();
  const rows = expanded ? ordered : ordered.slice(0, TOP_ROWS);

  return (
    <div className="screen">
      <h1>Statistiques</h1>
      <p className="lede">
        Ce qui est sorti, sur la période de votre choix. Un constat, pas une consigne de
        jeu.
      </p>

      <div className="segmented" role="group" aria-label="Jeu">
        {GAME_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            aria-pressed={selected === key}
            onClick={() => {
              setSelected(key);
              setSeries('main');
              setExpanded(false);
              setCustomFrom('');
              setCustomTo('');
            }}
          >
            {GAMES[key].label}
          </button>
        ))}
      </div>

      <label className="field">Période</label>
      <div className="chips" role="group" aria-label="Période">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            type="button"
            className="chip"
            aria-pressed={period === p.key}
            onClick={() => setPeriod(p.key)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {period === 'custom' && (
        <div className="row" style={{ marginTop: 10 }}>
          <label className="daterange">
            <span className="faint small">du</span>
            <input
              type="date"
              value={customFrom}
              min={bounds?.from ?? undefined}
              max={bounds?.to ?? undefined}
              onChange={(e) => setCustomFrom(e.target.value)}
            />
          </label>
          <label className="daterange">
            <span className="faint small">au</span>
            <input
              type="date"
              value={customTo}
              min={bounds?.from ?? undefined}
              max={bounds?.to ?? undefined}
              onChange={(e) => setCustomTo(e.target.value)}
            />
          </label>
        </div>
      )}

      {all === undefined ? (
        <p className="dim" style={{ marginTop: 18 }}>
          Chargement…
        </p>
      ) : draws.length === 0 ? (
        <div className="empty">Aucun tirage sur cette période.</div>
      ) : (
        <>
          <p className="small dim" style={{ marginTop: 10 }}>
            {formatInt(draws.length)} tirage{draws.length > 1 ? 's' : ''} du{' '}
            {formatDateMedium((draws[0] as DrawRecord).date)} au{' '}
            {formatDateMedium((draws[draws.length - 1] as DrawRecord).date)}, soit{' '}
            {formatInt(draws.length * perDraw)} numéros tirés.
          </p>

          <h2>{order === 'most' ? 'Numéros les plus sortis' : 'Numéros les moins sortis'}</h2>

          <div className="row tight" style={{ marginBottom: 10 }}>
            <div className="segmented compact" role="group" aria-label="Série">
              <button
                type="button"
                aria-pressed={series === 'main'}
                onClick={() => {
                  setSeries('main');
                  setExpanded(false);
                }}
              >
                {g.k}/{g.n}
              </button>
              <button
                type="button"
                aria-pressed={series === 'bonus'}
                onClick={() => {
                  setSeries('bonus');
                  setExpanded(false);
                }}
              >
                {g.bonusLabel}
              </button>
            </div>
            <button
              type="button"
              className="ghost small"
              onClick={() => setOrder(order === 'most' ? 'least' : 'most')}
            >
              {order === 'most' ? 'Voir les moins sortis' : 'Voir les plus sortis'}
            </button>
          </div>

          <div className="card">
            <ol className="freq" start={1}>
              {rows.map((row) => (
                <li key={row.number}>
                  <span className="ball small">{String(row.number).padStart(2, '0')}</span>
                  <span className="freq-bar" aria-hidden="true">
                    <span style={{ width: `${Math.max(2, row.share * 100)}%` }} />
                  </span>
                  <span className="freq-count tabular">
                    <b>{formatInt(row.count)}</b>
                    <span className="faint"> / {formatDecimal(row.expected, 1)}</span>
                  </span>
                  <span
                    className="freq-z tabular faint"
                    title="Écart à l’effectif attendu, en écarts-types"
                  >
                    {row.z >= 0 ? '+' : '−'}
                    {formatDecimal(Math.abs(row.z), 1)} σ
                  </span>
                </li>
              ))}
            </ol>

            <div className="row" style={{ marginTop: 6 }}>
              <button
                type="button"
                className="ghost small"
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? 'Réduire' : `Afficher les ${formatInt(nmax)} numéros`}
              </button>
            </div>

            <p className="small faint" style={{ marginBottom: 0 }}>
              La colonne du milieu compare les sorties observées à l’effectif attendu d’un
              tirage uniforme. La dernière donne l’écart en écarts-types.
            </p>
          </div>

          {windowTest !== null && (
            <div className="notice">
              Sur cette période, l’écart le plus fort est de{' '}
              <strong>{formatDecimal(windowTest.maxAbsZ, 2)} σ</strong>, pour un seuil de
              Bonferroni de {formatDecimal(windowTest.bonferroniZ, 2)} σ (α = 0,001 sur{' '}
              {formatInt(nmax)} numéros).{' '}
              {windowTest.maxAbsZ < windowTest.bonferroniZ
                ? 'Aucun numéro ne le dépasse : cet écart est celui que produit le hasard, et il ne dit rien du prochain tirage.'
                : 'Un numéro le dépasse. À confronter à la fenêtre glissante ci-dessous : sur une période courte choisie après coup, un dépassement isolé reste attendu.'}
              {draws.length < 100 && (
                <>
                  {' '}
                  <strong>
                    Moins de 100 tirages sur cette période : l’écart est d’autant plus large
                    que la fenêtre est courte.
                  </strong>
                </>
              )}
            </div>
          )}

          <h2>Ajustement à la loi uniforme</h2>
          <div className="card">
            <div className="card-head">
              <span className="card-title">Sur la période affichée</span>
              <span className={`pill ${(windowTest?.p ?? 0) > 0.05 ? 'ok' : 'muted'}`}>
                {(windowTest?.p ?? 0) > 0.05 ? 'conforme' : 'à surveiller'}
              </span>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Série</th>
                    <th>χ²</th>
                    <th>ddl</th>
                    <th>p</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      {series === 'main' ? `${g.k}/${g.n}` : g.bonusLabel}
                    </td>
                    <td>{formatDecimal(windowTest?.chi2 ?? Number.NaN, 2)}</td>
                    <td>{formatInt(windowTest?.df ?? 0)}</td>
                    <td>{formatDecimal(windowTest?.p ?? Number.NaN, 4)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <h2>Surveillance de dérive</h2>
          <div className="card">
            <div className="card-sub">
              Fenêtres glissantes de {WINDOW_MONTHS} mois sur les numéros principaux,
              indépendantes de la période choisie ci-dessus.
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Fenêtre</th>
                    <th>Tirages</th>
                    <th>χ²</th>
                    <th>p</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>courante</td>
                    <td>{formatInt(drift?.current.draws ?? 0)}</td>
                    <td>{formatDecimal(drift?.current.chi2 ?? Number.NaN, 2)}</td>
                    <td>{formatDecimal(drift?.current.p ?? Number.NaN, 4)}</td>
                  </tr>
                  <tr>
                    <td>précédente</td>
                    <td>{formatInt(drift?.previous?.draws ?? 0)}</td>
                    <td>{formatDecimal(drift?.previous?.chi2 ?? Number.NaN, 2)}</td>
                    <td>{formatDecimal(drift?.previous?.p ?? Number.NaN, 4)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            {drift?.previous != null &&
            drift.current.p < ALERT_P &&
            drift.previous.p < ALERT_P ? (
              <div className="notice warn" style={{ marginBottom: 0 }}>
                <strong>Alerte de dérive.</strong> p &lt; 0,001 sur deux fenêtres
                consécutives. C’est le seul motif légitime de regarder l’historique.
              </div>
            ) : (
              <p className="small dim" style={{ marginBottom: 0 }}>
                Aucune alerte. Elle se déclencherait à p &lt; 0,001 sur deux fenêtres
                consécutives.
              </p>
            )}
          </div>

          <p className="footnote">
            Les tirages sont indépendants : un numéro souvent sorti n’a pas plus de chances
            de revenir, un numéro peu sorti n’est pas « attendu ». C’est ce que mesure le
            test ci-dessus, et c’est pourquoi le générateur ne consulte jamais ces
            effectifs — il pondère les numéros selon ce que <em>les joueurs</em> choisissent,
            jamais selon ce qui est sorti.
          </p>
        </>
      )}
    </div>
  );
}
